import React, { useState, useEffect, useCallback } from 'react';
import { fetchEngagementReport, fetchAllEngagementRows } from '../services/authService';
import { exportTableAsCSV, generateExportFilename } from '../utils/exportUtils';
import {
  Search, RefreshCw, ChevronLeft, ChevronRight,
  Download, BarChart3, Clock, LogIn, CalendarDays, Users
} from 'lucide-react';

// Format minutes to readable duration (with decimal)
function formatDuration(minutes) {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = (minutes % 60).toFixed(0);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// Format date for display (IST)
function formatDate(dateStr) {
  if (!dateStr) return '—';
  // Backend returns datetime from MySQL. Format as IST.
  const d = new Date(dateStr);
  // Convert to IST using Intl formatter
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d).toUpperCase();
}

export default function EngagementReport() {
  const [report,     setReport]     = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const [search,     setSearch]     = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [sortBy,     setSortBy]     = useState('last_login');
  const [sortDir,    setSortDir]    = useState('desc');
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // default: show only active users
  const [daysRange,  setDaysRange]  = useState(30);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  const loadReport = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchEngagementReport({
        page,
        limit: pagination.limit,
        search: debouncedSearch,
        sort_by: sortBy,
        sort_dir: sortDir,
        role: roleFilter,
        status: statusFilter,
        days_range: daysRange,
      });
      setReport(data.report);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load engagement report');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, sortBy, sortDir, pagination.limit, roleFilter, statusFilter, daysRange]);

  useEffect(() => { loadReport(1); }, [debouncedSearch, sortBy, sortDir, roleFilter, statusFilter, daysRange]); // eslint-disable-line

  // Sort handler
  const handleSort = (column) => {
    if (sortBy === column) {
      if (sortDir === 'desc') setSortDir('asc');
      else { setSortBy('last_login'); setSortDir('desc'); }
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
  };

  const SortArrow = ({ column }) => {
    if (sortBy !== column) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-emerald-600 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // Export CSV — every user matching the current filters, not just this page.
  // Exporting the page would quietly hand over 50 rows out of however many
  // there are, and the file gives no hint that anyone is missing.
  const handleExportCSV = async () => {
    setExporting(true);
    setExportProgress('');
    setError('');
    try {
      const { rows: allRows } = await fetchAllEngagementRows(
        {
          search: debouncedSearch,
          sort_by: sortBy,
          sort_dir: sortDir,
          role: roleFilter,
          status: statusFilter,
          days_range: daysRange,
        },
        (loaded, total) => setExportProgress(`${loaded} of ${total}`)
      );

      const headers = [
        'User Name', 'Email', 'Role', 'Status', 'Last Login',
        `Total Time (${daysRange}d) in min`, `Logins Count (${daysRange}d)`, 'Logins Count (Total)',
        'Avg Time Spent (min)',
        'Personas Total (min)', 'Personas Avg (min)', 'Personas Visits',
        'Benchmarking Total (min)', 'Benchmarking Avg (min)', 'Benchmarking Visits',
      ];
      const rows = allRows.map(r => [
        r.user_name,
        r.email,
        r.role,
        r.is_active ? 'Active' : 'Inactive',
        r.last_login ? formatDate(r.last_login) : 'Never',
        r.total_time_spent > 0 ? r.total_time_spent : '0',
        r.logins_30d,
        r.logins_total,
        r.avg_time_spent != null ? r.avg_time_spent : 'N/A',
        r.personas_time > 0 ? r.personas_time : '0',
        r.personas_avg > 0 ? r.personas_avg : '0',
        r.personas_visits || 0,
        r.benchmarking_time > 0 ? r.benchmarking_time : '0',
        r.benchmarking_avg > 0 ? r.benchmarking_avg : '0',
        r.benchmarking_visits || 0,
      ]);

      const filename = generateExportFilename('Engagement_Report');
      exportTableAsCSV(headers, rows, filename);
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
      setExportProgress('');
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-emerald-600" />
            Login & Engagement Report
          </h1>
          <p className="text-sm text-gray-500 mt-1">{pagination.total} users</p>
        </div>
        <div className="flex items-center gap-2">
          {expandedUsers.size > 0 && (
            <button
              onClick={() => setExpandedUsers(new Set())}
              className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <span>Close All</span>
            </button>
          )}
          <button
            onClick={handleExportCSV}
            disabled={report.length === 0 || exporting}
            title={`Exports all ${pagination.total} users matching the current filters`}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
            <span>
              {exporting
                ? `Exporting${exportProgress ? ` ${exportProgress}` : ''}...`
                : `Export CSV (${pagination.total})`}
            </span>
          </button>
          <button
            onClick={() => loadReport(pagination.page)}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap items-center">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        {/* Role filter */}
        <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">All Roles</option>
          <option value="superadmin">Superadmin</option>
          <option value="stakeholder">Stakeholder</option>
          <option value="sales">Sales</option>
          <option value="physician">Physician</option>
        </select>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="active">Active Users</option>
          <option value="inactive">Inactive Users</option>
          <option value="">All Users</option>
        </select>

        {/* Days range */}
        <div className="flex items-center gap-1.5">
          {[7, 30, 90, 180].map(days => (
            <button
              key={days}
              onClick={() => setDaysRange(days)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                daysRange === days
                  ? 'bg-emerald-50 border-emerald-400 text-emerald-700'
                  : 'border-gray-300 text-gray-500 hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              {days}d
            </button>
          ))}
        </div>

        {/* Clear all filters */}
        {(search || roleFilter || statusFilter !== 'active' || daysRange !== 30) && (
          <button
            onClick={() => { setSearch(''); setRoleFilter(''); setStatusFilter('active'); setDaysRange(30); }}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span>×</span>
            Reset
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th onClick={() => handleSort('user_name')} className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none transition-colors" style={{ width: '22%' }}>
                  <div className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5 text-gray-400" />
                    User <SortArrow column="user_name" />
                  </div>
                </th>
                <th onClick={() => handleSort('last_login')} className="text-center px-4 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none transition-colors" style={{ width: '18%' }}>
                  <div className="flex items-center justify-center gap-1">
                    <CalendarDays className="w-3.5 h-3.5 text-gray-400" />
                    Last Login <SortArrow column="last_login" />
                  </div>
                </th>
                <th onClick={() => handleSort('logins_total')} className="text-center px-4 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none transition-colors" style={{ width: '12%' }}>
                  Logins Count (Total) <SortArrow column="logins_total" />
                </th>
                <th onClick={() => handleSort('logins_30d')} className="text-center px-4 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none transition-colors" style={{ width: '12%' }}>
                  Logins Count ({daysRange}d) <SortArrow column="logins_30d" />
                </th>
                <th onClick={() => handleSort('total_time_spent')} className="text-center px-4 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none transition-colors" style={{ width: '14%' }}>
                  Total Time ({daysRange}d) <SortArrow column="total_time_spent" />
                </th>
                <th onClick={() => handleSort('avg_time_spent')} className="text-center px-4 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none transition-colors" style={{ width: '14%' }}>
                  Avg Session Duration <SortArrow column="avg_time_spent" />
                </th>
                <th className="text-center px-4 py-3 font-medium text-gray-600" style={{ width: '8%' }}>
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : report.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-400">No users found</td>
                </tr>
              ) : report.map(row => (
                <React.Fragment key={row.user_id}>
                  <tr
                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${!row.last_login ? 'bg-amber-50/50' : ''}`}
                    onClick={() => setExpandedUsers(prev => {
                      const next = new Set(prev);
                      if (next.has(row.user_id)) next.delete(row.user_id);
                      else next.add(row.user_id);
                      return next;
                    })}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900 text-sm">{row.user_name}</p>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 capitalize">{row.role}</span>
                        </div>
                        <p className="text-xs text-gray-500">{row.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.last_login ? (
                        <span className="text-xs text-gray-700">{formatDate(row.last_login)}</span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Never</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        row.logins_total >= 20 ? 'bg-emerald-50 text-emerald-700' :
                        row.logins_total >= 5 ? 'bg-blue-50 text-blue-700' :
                        row.logins_total > 0 ? 'bg-gray-100 text-gray-600' :
                        'bg-gray-100 text-gray-400'
                      }`}>
                        {row.logins_total}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        row.logins_30d >= 20 ? 'bg-emerald-50 text-emerald-700' :
                        row.logins_30d >= 5 ? 'bg-blue-50 text-blue-700' :
                        row.logins_30d > 0 ? 'bg-gray-100 text-gray-600' :
                        'bg-gray-100 text-gray-400'
                      }`}>
                        {row.logins_30d}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.total_time_spent > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                          {formatDuration(row.total_time_spent)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.avg_time_spent != null && row.avg_time_spent > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          {formatDuration(row.avg_time_spent)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform mx-auto ${expandedUsers.has(row.user_id) ? 'rotate-90' : ''}`} />
                    </td>
                  </tr>

                  {/* Expanded Module Breakdown */}
                  {expandedUsers.has(row.user_id) && (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                        <div className="grid grid-cols-2 gap-4 max-w-lg">
                          {/* Patient Personas */}
                          <div className="bg-white rounded-lg p-3 border border-teal-100">
                            <p className="text-[10px] font-bold text-teal-600 uppercase tracking-wider mb-2">Patient Personas</p>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-[10px] text-slate-400">Total</p>
                                <p className="text-sm font-bold text-slate-800">
                                  {row.personas_time > 0 ? formatDuration(row.personas_time) : '—'}
                                </p>
                              </div>
                              <div className="text-center">
                                <p className="text-[10px] text-slate-400">Visits</p>
                                <p className="text-sm font-bold text-slate-800">
                                  {row.personas_visits || 0}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-[10px] text-slate-400">Avg / visit</p>
                                <p className="text-sm font-bold text-slate-800">
                                  {row.personas_avg > 0 ? formatDuration(row.personas_avg) : '—'}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Benchmarking */}
                          <div className="bg-white rounded-lg p-3 border border-purple-100">
                            <p className="text-[10px] font-bold text-purple-600 uppercase tracking-wider mb-2">Benchmarking</p>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-[10px] text-slate-400">Total</p>
                                <p className="text-sm font-bold text-slate-800">
                                  {row.benchmarking_time > 0 ? formatDuration(row.benchmarking_time) : '—'}
                                </p>
                              </div>
                              <div className="text-center">
                                <p className="text-[10px] text-slate-400">Visits</p>
                                <p className="text-sm font-bold text-slate-800">
                                  {row.benchmarking_visits || 0}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-[10px] text-slate-400">Avg / visit</p>
                                <p className="text-sm font-bold text-slate-800">
                                  {row.benchmarking_avg > 0 ? formatDuration(row.benchmarking_avg) : '—'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} users)
            </span>
            <div className="flex items-center space-x-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => loadReport(pagination.page - 1)}
                className="p-1.5 rounded border border-gray-300 disabled:opacity-40 hover:bg-white transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => loadReport(pagination.page + 1)}
                className="p-1.5 rounded border border-gray-300 disabled:opacity-40 hover:bg-white transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
